import { useState, useEffect } from 'react';
import {
    Play,
    CheckCircle,
    XCircle,
    ArrowRight,
    Trophy,
    BookOpen,
    HelpCircle,
    RotateCcw,
    Home,
    Medal,
    Star,
    Award,
    Activity,
    Users
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ope } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import confetti from 'canvas-confetti';

const QuizTab = () => {
    const [mode, setMode] = useState('menu'); // menu, quiz, summary, reto_summary
    const [temas, setTemas] = useState([]);
    const [selectedTema, setSelectedTema] = useState(null);
    const [preguntas, setPreguntas] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedOption, setSelectedOption] = useState(null);
    const [isAnswered, setIsAnswered] = useState(false);
    const [score, setScore] = useState(0); // Aciertos
    const [fallos, setFallos] = useState(0); // Añadido fallos
    const [loading, setLoading] = useState(false);
    
    // Estado específico del Reto Diario
    const [retoStatus, setRetoStatus] = useState(null); // { available, completedToday, result, config }
    const [retoRanking, setRetoRanking] = useState(null);
    const [isRetoMode, setIsRetoMode] = useState(false);
    const [puntosReto, setPuntosReto] = useState(0);

    const toast = useToast();

    useEffect(() => {
        loadTemas();
        loadRetoStatus();
    }, []);

    const loadRetoStatus = async () => {
        try {
            const data = await ope.getRetoDiario();
            setRetoStatus(data);
            if (data.available && data.completedToday) {
                loadRetoRanking();
            }
        } catch (error) {
            console.error('Error cargando estado del reto:', error);
        }
    };

    const loadRetoRanking = async () => {
        try {
            const data = await ope.getRetoDiarioRanking();
            setRetoRanking(data);
        } catch (error) {
            console.error('Error cargando ranking:', error);
        }
    };

    const loadTemas = async () => {
        try {
            const data = await ope.getTemas();
            setTemas(data);
        } catch (error) {
            console.error(error);
            toast.error('Error al cargar temas');
        }
    };

    const startQuiz = async (tema) => {
        setLoading(true);
        setSelectedTema(tema);
        try {
            const data = await ope.getPreguntasTema(tema.id);
            if (data.length === 0) {
                toast.info('No hay preguntas disponibles para este tema aún.');
                setLoading(false);
                return;
            }
            setPreguntas(data);
            setCurrentIndex(0);
            setScore(0);
            setMode('quiz');
            resetQuestionState();
        } catch (error) {
            console.error(error);
            toast.error('Error al iniciar el test');
        } finally {
            setLoading(false);
        }
    };

    const resetQuestionState = () => {
        setSelectedOption(null);
        setIsAnswered(false);
    };

    const handleAnswer = async (option) => {
        if (isAnswered) return;

        setSelectedOption(option);
        setIsAnswered(true);

        const currentQuestion = preguntas[currentIndex];
        const isCorrect = option === currentQuestion.respuesta_correcta;

        if (isCorrect) {
            setScore(prev => prev + 1);
            if (!isRetoMode) {
                confetti({
                    particleCount: 50,
                    spread: 60,
                    origin: { y: 0.7 },
                    colors: ['#22c55e', '#4ade80']
                });
            }
        } else {
            setFallos(prev => prev + 1);
        }

        // Send to backend for SM-2 (solo si NO es reto diario o si queremos guardar progreso SM-2 en retos)
        if (!isRetoMode) {
            try {
                await ope.responder({
                    pregunta_id: currentQuestion.id,
                    respuesta_dada: option,
                    es_correcta: isCorrect
                });
            } catch (error) {
                console.error('Error saving answer:', error);
            }
        }
    };

    const nextQuestion = async () => {
        if (currentIndex < preguntas.length - 1) {
            setCurrentIndex(prev => prev + 1);
            resetQuestionState();
        } else {
            // Fin del test
            if (isRetoMode) {
                await finishReto();
            } else {
                setMode('summary');
                if (score === preguntas.length) {
                    confetti({ particleCount: 150, spread: 100, origin: { y: 0.6 } });
                }
            }
        }
    };

    const finishReto = async () => {
        setLoading(true);
        try {
            // Enviar resultado del reto
            const result = await ope.completarRetoDiario({
                aciertos: score,
                fallos: fallos
            });
            setPuntosReto(result.puntos_obtenidos);
            await loadRetoRanking(); // Refresh ranking
            setMode('reto_summary');
            confetti({ particleCount: 150, spread: 100, origin: { y: 0.6 } });
            
            // Refrescar estado del reto menu
            await loadRetoStatus();
        } catch (error) {
            console.error('Error al finalizar reto:', error);
            toast.error(error.message || 'Error al guardar el resultado del reto');
            setMode('menu'); // Fallback
        } finally {
            setLoading(false);
        }
    };

    const startReto = () => {
        if (!retoStatus || !retoStatus.preguntas) return;
        
        setIsRetoMode(true);
        setPreguntas(retoStatus.preguntas);
        setCurrentIndex(0);
        setScore(0);
        setFallos(0);
        setMode('quiz');
        resetQuestionState();
    };

    const goToMenu = () => {
        setMode('menu');
        setPreguntas([]);
        setSelectedTema(null);
        setIsRetoMode(false);
    };

    if (loading) {
        return <div className="p-12 text-center text-gray-400">Cargando...</div>;
    }

    // --- MENU MODE ---
    if (mode === 'menu') {
        const isRetoAvailable = retoStatus?.available && !retoStatus.completedToday;
        
        return (
            <div className="grid gap-4 md:grid-cols-2">
                {/* ── Reto Diario Card ── */}
                {retoStatus?.available ? (
                    <div className="md:col-span-2 bg-gradient-to-r from-purple-600 to-indigo-600 p-6 rounded-2xl border border-purple-400/30 mb-4 shadow-lg overflow-hidden relative">
                        {/* Decoración bg */}
                        <div className="absolute -right-10 -top-10 text-white/10 rotate-12 pointer-events-none">
                            <Trophy className="w-48 h-48" />
                        </div>
                        
                        <div className="relative z-10">
                            <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                                <Trophy className="w-5 h-5 text-yellow-300" />
                                Reto Diario
                                {!isRetoAvailable && <span className="text-xs font-normal bg-green-500/20 text-green-200 px-2 py-0.5 rounded-full border border-green-400/30">Completado hoy</span>}
                            </h3>
                            
                            {isRetoAvailable ? (
                                <>
                                    <p className="text-purple-100 text-sm mb-4 max-w-md">
                                        {retoStatus.config?.preguntas || 8} preguntas de todos los temas. Gana puntos por participar y por cada acierto.
                                    </p>
                                    <div className="flex flex-wrap items-center gap-4">
                                        <button
                                            onClick={startReto}
                                            className="bg-white text-purple-900 px-6 py-2.5 rounded-xl font-bold text-sm hover:bg-purple-50 hover:scale-105 transition-all shadow-md flex items-center gap-2"
                                        >
                                            <Play className="w-4 h-4" />
                                            Jugar Reto ({retoStatus.config?.preguntas || 8} preg)
                                        </button>
                                        <div className="flex gap-3 text-xs text-white/80 font-medium">
                                            <span className="flex items-center gap-1"><Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-500" />+{retoStatus.config?.pts_participacion} pt base</span>
                                            <span className="flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5 text-green-400" />+{retoStatus.config?.pts_acierto} pt acierto</span>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="flex flex-row gap-3 sm:gap-6 items-stretch mt-2 overflow-hidden">
                                    <div className="flex-1 min-w-0 flex flex-col">
                                        <div className="bg-black/30 rounded-xl p-3 h-full flex flex-col justify-center shadow-inner">
                                            <p className="text-purple-200 text-[10px] sm:text-xs font-medium mb-1.5 uppercase tracking-wide truncate">Tu resultado hoy</p>
                                            <div className="flex gap-2.5 sm:gap-3 items-center">
                                                <div className="text-green-300 font-bold flex items-center gap-1 text-sm"><CheckCircle className="w-4 h-4" /> {retoStatus.resultado?.aciertos}</div>
                                                <div className="text-red-300 font-bold flex items-center gap-1 text-sm"><XCircle className="w-4 h-4" /> {retoStatus.resultado?.fallos}</div>
                                                <div className="text-yellow-300 font-black flex items-center justify-center bg-yellow-400/20 px-1.5 py-0.5 rounded text-xs ring-1 ring-yellow-400/50 shadow-sm whitespace-nowrap">{retoStatus.resultado?.puntos} pts</div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Ranking Preview */}
                                    {retoRanking?.top3?.length > 0 && (
                                        <div className="flex-[1.2] min-w-0 pl-3 sm:pl-4 border-l border-purple-400/30 flex flex-col justify-center">
                                            <div className="flex justify-between items-center mb-2">
                                                <h4 className="text-white text-xs sm:text-sm font-medium flex items-center gap-1.5 truncate">
                                                    <Award className="w-3.5 h-3.5 text-purple-200 shrink-0" /> 
                                                    <span className="truncate">Top {retoStatus.config?.reset_periodo}</span>
                                                </h4>
                                                <button 
                                                    onClick={() => setMode('reto_summary')}
                                                    className="text-[10px] sm:text-xs font-semibold text-purple-100 hover:text-white bg-purple-500/30 hover:bg-purple-500/50 px-2 py-1 rounded-lg transition-all whitespace-nowrap"
                                                >
                                                    Ver más
                                                </button>
                                            </div>
                                            
                                            {/* Top 1 Only on very small screens to save space, but we have a flex wrapper */}
                                            <div className="flex flex-col gap-1.5 overflow-hidden">
                                                {retoRanking.top3.slice(0, 1).map((user, i) => (
                                                    <div key={user.user_id} className={`flex items-center gap-2 p-1.5 px-2.5 rounded-lg border ${user.isMe ? 'bg-purple-500/50 border-purple-300 text-white' : 'bg-black/20 border-white/10 text-purple-100'}`}>
                                                        <span className="font-black text-xs text-yellow-400">#1</span>
                                                        <span className="text-xs font-medium truncate flex-1" title={user.nombre}>{user.isMe ? 'Tú' : user.nombre}</span>
                                                        <span className="text-[10px] font-bold opacity-90 whitespace-nowrap">{user.puntos} pts</span>
                                                    </div>
                                                ))}
                                            </div>
                                            
                                            {retoRanking.me && (
                                                <p className="text-[10px] text-purple-200 mt-2 truncate">
                                                    Tu posición: <strong className="text-white">#{retoRanking.me.posicion}</strong> de {retoRanking.totalParticipantes}
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                ) : retoStatus?.message ? (
                    <div className="md:col-span-2 bg-[var(--glass-bg)] p-6 rounded-2xl border border-[var(--glass-border)] mb-4 text-center">
                        <Trophy className="w-8 h-8 text-gray-400 mx-auto mb-2 opacity-50" />
                        <h3 className="text-[var(--text-primary)] font-medium mb-1">Reto Diario</h3>
                        <p className="text-[var(--text-muted)] text-sm">{retoStatus.message}</p>
                    </div>
                ) : null}

                <h3 className="text-lg font-semibold text-[var(--text-primary)] md:col-span-2 mt-2">Practicar por Tema</h3>
                {temas.filter(t => t.num_publicadas > 0).map(tema => (
                    <button
                        key={tema.id}
                        onClick={() => startQuiz(tema)}
                        className="bg-[var(--bg-secondary)] border border-[var(--glass-border)] p-4 rounded-xl text-left hover:bg-[var(--glass-bg)] transition-all group shadow-sm"
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <span className="text-xs font-bold text-indigo-500 block mb-1">TEMA {tema.orden}</span>
                                <h4 className="font-medium text-[var(--text-primary)] group-hover:text-indigo-400 transition-colors">{tema.titulo}</h4>
                            </div>
                            <Play className="w-5 h-5 text-[var(--text-muted)] group-hover:text-[var(--text-primary)] transition-colors" />
                        </div>
                    </button>
                ))}
                {temas.filter(t => t.num_publicadas > 0).length === 0 && (
                    <div className="md:col-span-2 text-center text-gray-500 py-10">
                        No hay temas activos o con preguntas disponibles para practicar.
                    </div>
                )}
            </div>
        );
    }

    // --- SUMMARY MODE ---
    if (mode === 'summary') {
        const percentage = Math.round((score / preguntas.length) * 100);

        return (
            <div className="text-center py-10">
                <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-tr from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-purple-500/30">
                    <Trophy className="w-12 h-12 text-white" />
                </div>

                <h2 className="text-3xl font-bold text-[var(--text-primary)] mb-2">
                    Test Completado
                </h2>
                <p className="text-[var(--text-muted)] mb-8">Has terminado el repaso del tema {selectedTema?.titulo}</p>

                <div className="grid grid-cols-2 gap-4 max-w-sm mx-auto mb-8">
                    <div className="bg-[var(--bg-secondary)] p-4 rounded-xl border border-[var(--glass-border)]">
                        <div className="text-3xl font-bold text-green-500">{score}</div>
                        <div className="text-xs text-[var(--text-muted)]">Aciertos</div>
                    </div>
                    <div className="bg-[var(--bg-secondary)] p-4 rounded-xl border border-[var(--glass-border)]">
                        <div className={`text-3xl font-bold ${percentage >= 50 ? 'text-blue-500' : 'text-red-500'}`}>
                            {percentage}%
                        </div>
                        <div className="text-xs text-[var(--text-muted)]">Puntuación</div>
                    </div>
                </div>
            </div>
        );
    }

    // --- RETO SUMMARY MODE ---
    if (mode === 'reto_summary') {
        return (
            <div className="max-w-md mx-auto py-8">
                <div className="text-center mb-8">
                    <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-tr from-yellow-400 to-amber-600 flex items-center justify-center shadow-lg shadow-yellow-500/30">
                        <Star className="w-10 h-10 text-white fill-white" />
                    </div>
                    <h2 className="text-3xl font-black text-[var(--text-primary)] mb-1">
                        {isRetoMode ? '¡Reto Completado!' : 'Resultados del Reto'}
                    </h2>
                    <p className="text-[var(--text-muted)]">Has ganado <strong className="text-yellow-500">+{retoStatus?.resultado?.puntos ?? puntosReto} puntos</strong> para el ranking semanal.</p>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-8">
                    <div className="bg-[var(--bg-secondary)] p-4 rounded-xl border border-[var(--glass-border)] text-center">
                        <div className="text-3xl font-bold text-green-500 flex items-center justify-center gap-1 mb-1">
                            <CheckCircle className="w-5 h-5" /> {retoStatus?.resultado?.aciertos ?? score}
                        </div>
                        <div className="text-xs text-[var(--text-muted)] uppercase tracking-wider font-semibold">Aciertos</div>
                    </div>
                    <div className="bg-[var(--bg-secondary)] p-4 rounded-xl border border-[var(--glass-border)] text-center">
                        <div className="text-3xl font-bold text-red-500 flex items-center justify-center gap-1 mb-1">
                            <XCircle className="w-5 h-5" /> {retoStatus?.resultado?.fallos ?? fallos}
                        </div>
                        <div className="text-xs text-[var(--text-muted)] uppercase tracking-wider font-semibold">Fallos</div>
                    </div>
                </div>

                {/* Ranking Leaderboard */}
                {retoRanking && (
                    <div className="bg-[var(--glass-bg)] rounded-3xl p-6 border border-[var(--black-border)] mb-8 shadow-2xl relative overflow-hidden">
                        {/* Background Decoration */}
                        <div className="absolute top-0 right-0 -mr-8 -mt-8 opacity-5">
                            <Trophy className="w-40 h-40" />
                        </div>

                        <div className="relative z-10">
                            <h3 className="text-xl font-bold text-[var(--text-primary)] mb-8 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Activity className="w-5 h-5 text-indigo-400" />
                                    Ranking Semanal
                                </div>
                                <div className="text-xs font-medium text-[var(--text-muted)] bg-[var(--bg-secondary)] px-3 py-1 rounded-full border border-[var(--glass-border)]">
                                    Top 100
                                </div>
                            </h3>
                            
                            {/* Podium Section */}
                            {retoRanking.top3 && retoRanking.top3.length > 0 && (
                                <div className="flex items-end justify-center gap-2 mb-10 h-44 px-2">
                                    {/* 2nd Place */}
                                    {retoRanking.top3[1] && (
                                        <motion.div 
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            transition={{ type: 'spring', damping: 15, delay: 0.2 }}
                                            className="flex flex-col items-center flex-1 max-w-[100px]"
                                        >
                                            <div className="relative mb-2">
                                                <div className={`w-12 h-12 rounded-full border-2 ${retoRanking.top3[1].isMe ? 'border-indigo-400 ring-2 ring-indigo-500/20' : 'border-slate-400'} overflow-hidden bg-slate-800 flex items-center justify-center shadow-lg`}>
                                                    <span className="text-xl font-bold text-slate-300">2</span>
                                                </div>
                                                <div className="absolute -top-1 -right-1 bg-slate-500 text-white w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold border border-white">#2</div>
                                            </div>
                                            <div className={`w-full ${retoRanking.top3[1].isMe ? 'bg-gradient-to-t from-indigo-700 to-indigo-500' : 'bg-gradient-to-t from-slate-700 to-slate-500'} h-20 rounded-t-xl flex flex-col items-center justify-center p-2 shadow-lg border-x border-t border-white/10`}>
                                                <span className="text-white text-[10px] font-bold truncate w-full text-center px-1 mb-0.5">{retoRanking.top3[1].isMe ? 'Tú' : retoRanking.top3[1].nombre}</span>
                                                <span className="text-white/80 text-[10px] font-mono">{retoRanking.top3[1].puntos}</span>
                                            </div>
                                        </motion.div>
                                    )}

                                    {/* 1st Place */}
                                    {retoRanking.top3[0] && (
                                        <motion.div 
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            transition={{ type: 'spring', damping: 12 }}
                                            className="flex flex-col items-center flex-1 max-w-[110px] z-10"
                                        >
                                            <div className="relative mb-2">
                                                <div className={`w-16 h-16 rounded-full border-4 ${retoRanking.top3[0].isMe ? 'border-yellow-300 ring-4 ring-yellow-500/30' : 'border-yellow-500'} overflow-hidden bg-slate-800 flex items-center justify-center shadow-2xl`}>
                                                    <Trophy className="w-8 h-8 text-yellow-500" />
                                                </div>
                                                <div className="absolute -top-2 -right-2 bg-yellow-400 text-slate-900 w-7 h-7 rounded-full flex items-center justify-center text-xs font-black border-2 border-white shadow-lg">#1</div>
                                            </div>
                                            <div className={`w-full ${retoRanking.top3[0].isMe ? 'bg-gradient-to-t from-indigo-700 to-indigo-500' : 'bg-gradient-to-t from-yellow-600 to-yellow-400'} h-32 rounded-t-xl flex flex-col items-center justify-center p-2 shadow-2xl border-x border-t border-white/20 ring-1 ring-yellow-300/30`}>
                                                <span className="text-white text-xs font-black truncate w-full text-center px-1 mb-1">{retoRanking.top3[0].isMe ? 'Tú' : retoRanking.top3[0].nombre}</span>
                                                <div className="bg-black/20 px-2 py-0.5 rounded-full flex items-center gap-1">
                                                    <Star className="w-2.5 h-2.5 text-yellow-400 fill-yellow-400" />
                                                    <span className="text-yellow-100 text-[10px] font-bold font-mono">{retoRanking.top3[0].puntos}</span>
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}

                                    {/* 3rd Place */}
                                    {retoRanking.top3[2] && (
                                        <motion.div 
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            transition={{ type: 'spring', damping: 15, delay: 0.4 }}
                                            className="flex flex-col items-center flex-1 max-w-[100px]"
                                        >
                                            <div className="relative mb-2">
                                                <div className={`w-12 h-12 rounded-full border-2 ${retoRanking.top3[2].isMe ? 'border-indigo-400 ring-2 ring-indigo-500/20' : 'border-amber-700'} overflow-hidden bg-slate-800 flex items-center justify-center shadow-lg`}>
                                                    <span className="text-xl font-bold text-amber-600">3</span>
                                                </div>
                                                <div className="absolute -top-1 -right-1 bg-amber-800 text-white w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold border border-white">#3</div>
                                            </div>
                                            <div className={`w-full ${retoRanking.top3[2].isMe ? 'bg-gradient-to-t from-indigo-700 to-indigo-500' : 'bg-gradient-to-t from-amber-800 to-amber-700'} h-16 rounded-t-xl flex flex-col items-center justify-center p-2 shadow-lg border-x border-t border-white/10`}>
                                                <span className="text-white text-[10px] font-bold truncate w-full text-center px-1 mb-0.5">{retoRanking.top3[2].isMe ? 'Tú' : retoRanking.top3[2].nombre}</span>
                                                <span className="text-white/80 text-[10px] font-mono">{retoRanking.top3[2].puntos}</span>
                                            </div>
                                        </motion.div>
                                    )}
                                </div>
                            )}

                            <div className="space-y-2 mt-4">
                                {retoRanking.top3.map((user, i) => (
                                    <div key={user.user_id} className={`flex items-center justify-between p-3 rounded-xl transition-all ${user.isMe ? 'bg-indigo-600/30 border border-indigo-500/50 shadow-lg shadow-indigo-500/10' : 'bg-[var(--bg-primary)] border border-[var(--glass-border)]'}`}>
                                        <div className="flex items-center gap-3">
                                            <div className={`w-6 h-6 rounded-full flex items-center justify-center font-bold text-[10px]
                                                ${i === 0 ? 'bg-yellow-500/20 text-yellow-500' : 
                                                  i === 1 ? 'bg-slate-500/20 text-slate-400' : 
                                                  i === 2 ? 'bg-amber-600/20 text-amber-600' : 'bg-gray-500/10 text-gray-500'}`}>
                                                {i + 1}
                                            </div>
                                            <span className={`text-sm ${user.isMe ? 'font-bold text-white' : 'text-[var(--text-primary)]'}`}>{user.isMe ? 'Tú' : user.nombre}</span>
                                        </div>
                                        <div className="font-mono text-sm font-bold text-[var(--text-primary)]">
                                            {user.puntos}
                                        </div>
                                    </div>
                                ))}

                                {/* Show "Me" if not in top 3 */}
                                {retoRanking.me && retoRanking.me.posicion > 3 && (
                                    <>
                                        <div className="flex justify-center py-1 opacity-50">
                                            <div className="h-4 border-l-2 border-dotted border-[var(--text-muted)]"></div>
                                        </div>
                                        <div className="flex items-center justify-between p-3 rounded-xl bg-indigo-600/30 border border-indigo-500/50 shadow-lg shadow-indigo-500/10">
                                            <div className="flex items-center gap-3">
                                                <div className="w-6 h-6 rounded-full flex items-center justify-center font-bold text-[10px] bg-indigo-500 text-white">
                                                    {retoRanking.me.posicion}
                                                </div>
                                                <span className="text-sm font-bold text-white">Tu posición</span>
                                            </div>
                                            <div className="font-mono text-sm font-bold text-white">
                                                {retoRanking.me.puntos}
                                            </div>
                                        </div>
                                    </>
                                )}
                                
                                {retoRanking.me && (
                                    <div className="text-center mt-6 flex items-center justify-center gap-2 text-xs text-[var(--text-muted)]">
                                        <Users className="w-3.5 h-3.5" />
                                        <span>Participando: <strong>{retoRanking.totalParticipantes}</strong> conductores</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )  }

                <button
                    onClick={goToMenu}
                    className="w-full py-4 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold shadow-lg transition-colors flex items-center justify-center gap-2"
                >
                    <Home className="w-5 h-5" />
                    Volver al Menú
                </button>
            </div>
        );
    }

    // --- QUIZ MODE ---
    const question = preguntas[currentIndex];
    // Map internal options to array for rendering
    const options = [
        { key: 'A', text: question.opcion_a },
        { key: 'B', text: question.opcion_b },
        { key: 'C', text: question.opcion_c },
        { key: 'D', text: question.opcion_d },
    ];

    return (
        <div className="max-w-2xl mx-auto">
            {/* Progress */}
            <div className="flex items-center justify-between text-xs text-[var(--text-muted)] mb-4">
                <span>Pregunta {currentIndex + 1} de {preguntas.length}</span>
                <span className="flex gap-3">
                    <span className="text-green-500 font-bold whitespace-nowrap">Ac: {score}</span>
                    <span className="text-red-500 font-bold whitespace-nowrap">Fa: {fallos}</span>
                </span>
            </div>
            <div className="h-1 bg-gray-700 rounded-full mb-8 overflow-hidden flex">
                <div
                    className={`${isRetoMode ? 'bg-yellow-500' : 'bg-purple-500'} transition-all duration-300`}
                    style={{ width: `${((currentIndex + 1) / preguntas.length) * 100}%` }}
                />
            </div>

            {/* Question */}
            <div className="bg-[var(--bg-secondary)] border border-[var(--glass-border)] rounded-2xl p-6 mb-6 shadow-xl relative overflow-hidden">
                {isRetoMode && (
                    <div className="absolute top-0 right-0 p-2 opacity-10 pointer-events-none">
                        <Trophy className="w-20 h-20" />
                    </div>
                )}
                <h3 className="text-lg md:text-xl font-medium text-[var(--text-primary)] leading-relaxed relative z-10">
                    {question.pregunta}
                </h3>
            </div>

            {/* Options */}
            <div className="space-y-3">
                {options.map((opt) => {
                    let btnClass = "w-full p-4 rounded-xl text-left border transition-all duration-200 flex items-start gap-4 ";

                    if (!isAnswered) {
                        btnClass += "bg-[var(--bg-primary)] border-[var(--glass-border)] hover:bg-[var(--glass-bg)] text-[var(--text-primary)]";
                        if (selectedOption === opt.key) btnClass += " ring-2 ring-purple-500";
                    } else {
                        if (opt.key === question.respuesta_correcta) {
                            btnClass += "bg-green-500/10 border-green-500/50 text-green-600 dark:text-green-400 font-medium";
                        } else if (opt.key === selectedOption) {
                            btnClass += "bg-red-500/10 border-red-500/50 text-red-600 dark:text-red-400 font-medium";
                        } else {
                            btnClass += "bg-[var(--bg-primary)] border-[var(--glass-border)] text-[var(--text-muted)] opacity-50";
                        }
                    }

                    return (
                        <button
                            key={opt.key}
                            onClick={() => handleAnswer(opt.key)}
                            disabled={isAnswered}
                            className={btnClass}
                        >
                            <span className={`
                flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border
                ${!isAnswered
                                    ? 'border-gray-400 text-gray-500'
                                    : opt.key === question.respuesta_correcta
                                        ? 'bg-green-500 border-green-500 text-white'
                                        : opt.key === selectedOption
                                            ? 'bg-red-500 border-red-500 text-white'
                                            : 'border-gray-300 text-gray-400'
                                }
              `}>
                                {opt.key}
                            </span>
                            <span>{opt.text}</span>

                            {isAnswered && opt.key === question.respuesta_correcta && (
                                <CheckCircle className="w-5 h-5 ml-auto text-green-500" />
                            )}
                            {isAnswered && opt.key === selectedOption && opt.key !== question.respuesta_correcta && (
                                <XCircle className="w-5 h-5 ml-auto text-red-500" />
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Feedback & Next */}
            {isAnswered && (
                <div className="mt-8 animate-in fade-in slide-in-from-bottom-4 duration-300">
                    {question.explicacion && (
                        <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 mb-6">
                            <div className="flex items-start gap-3">
                                <BookOpen className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-sm font-bold text-blue-500 mb-1">Explicación</p>
                                    <p className="text-sm text-[var(--text-primary)]">{question.explicacion}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    <button
                        onClick={nextQuestion}
                        className={`w-full py-4 text-white rounded-xl font-bold text-lg shadow-lg transition-all flex items-center justify-center gap-2 ${
                            isRetoMode ? 'bg-indigo-600 hover:bg-indigo-500 shadow-indigo-900/30' : 'bg-purple-600 hover:bg-purple-500 shadow-purple-900/30'
                        }`}
                    >
                        {currentIndex < preguntas.length - 1 ? 'Siguiente Pregunta' : 'Ver Resultados'}
                        {currentIndex < preguntas.length - 1 ? <ArrowRight className="w-5 h-5" /> : <Trophy className="w-5 h-5" />}
                    </button>
                </div>
            )}
        </div>
    );
};

export default QuizTab;
